import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Edit2, RotateCcw } from 'lucide-react';

interface Employee {
  id: number;
  name: string;
  designation: string;
  in_time: string | null;
  out_time: string | null;
  status: string | null;
  remark: string | null;
}

// const API_URL = 'http://localhost:5551';
// const API_URL = 'https://attendance.nadezhda.in';
const API_URL = '/api';

const DailyAttendance = ({ isAdmin }: { isAdmin: boolean }) => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const todayStr = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState<{empId: number, in: string, out: string} | null>(null);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/employees?date=${selectedDate}`);
      setEmployees(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchEmployees(); }, [selectedDate]);

  const handleAttendance = async (empId: number, type: 'in' | 'out') => {
    const emp = employees.find(e => e.id === empId);
    const now = new Date().toLocaleTimeString('en-GB', { hour12: false });
    
    await axios.post(`${API_URL}/attendance/update`, {
      employee_id: empId, date: selectedDate,
      in_time: type === 'in' ? now : emp?.in_time,
      out_time: type === 'out' ? now : emp?.out_time
    });
    fetchEmployees();
  };

  const saveManualAttendance = async () => {
    if (!editData) return;
    await axios.post(`${API_URL}/attendance/update`, {
      employee_id: editData.empId, date: selectedDate,
      in_time: editData.in || null, out_time: editData.out || null
    });
    setIsEditModalOpen(false);
    fetchEmployees();
  };


  const handleReset = async (empId: number) => {
  if (window.confirm("Kya aap sach mein attendance reset karna chahte hain?")) {
    try {
      await axios.post(`${API_URL}/attendance/reset`, {
        employee_id: empId,
        date: selectedDate
      });
      fetchEmployees(); // List refresh karne ke liye
    } catch (err) {
      console.error("Reset Error:", err);
      alert("Reset fail ho gaya!");
    }
  }
};

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50/50">
        <div className="flex items-center gap-4">
          <span className="text-slate-600 font-semibold">Select Date:</span>
          <input type="date" value={selectedDate} max={todayStr} onChange={(e) => setSelectedDate(e.target.value)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm" />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-bold">
            <tr>
              <th className="px-6 py-4">Employee</th>
              <th className="px-6 py-4">In Time</th>
              <th className="px-6 py-4">Out Time</th>
              <th className="px-6 py-4">Status</th>
              <th className="px-6 py-4">Remark</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {employees.map(emp => (
              <tr key={emp.id} className="hover:bg-blue-50/30 transition-colors">
                <td className="px-6 py-4">
                  <div className="font-bold text-slate-900">{emp.name}</div>
                  <div className="text-xs text-slate-500">{emp.designation}</div>
                </td>
                <td className={`px-6 py-4 font-mono font-bold ${emp.in_time ? 'text-green-600' : 'text-slate-300'}`}>{emp.in_time || '--:--'}</td>
                <td className={`px-6 py-4 font-mono font-bold ${emp.out_time ? 'text-red-500' : 'text-slate-300'}`}>{emp.out_time || '--:--'}</td>
                <td className="px-6 py-4">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                    emp.status === 'Present' ? 'bg-green-100 text-green-700' :
                    emp.status === 'Half Day' ? 'bg-yellow-100 text-yellow-700' :
                    emp.status === 'Working' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                  }`}>{emp.status || 'ABSENT'}</span>
                </td>
                <td className="px-6 py-4 text-xs font-semibold text-red-400 italic">{emp.remark}</td>
                <td className="px-6 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => handleAttendance(emp.id, 'in')} disabled={!!emp.in_time || selectedDate !== todayStr} className="bg-green-600 hover:bg-green-700 disabled:opacity-20 text-white px-4 py-1.5 rounded-md text-xs font-bold shadow-sm">IN</button>
                    <button onClick={() => handleAttendance(emp.id, 'out')} disabled={!emp.in_time || !!emp.out_time || selectedDate !== todayStr} className="bg-red-600 hover:bg-red-700 disabled:opacity-20 text-white px-4 py-1.5 rounded-md text-xs font-bold shadow-sm">OUT</button>
                    {isAdmin && (
      <button 
        onClick={() => handleReset(emp.id)} 
        className="bg-orange-100 hover:bg-orange-200 text-orange-600 p-2 rounded-md transition-colors"
        title="Reset Attendance"
      >
        <RotateCcw size={14} />
      </button>
    )}
                    {isAdmin && (
                      <button onClick={() => { setEditData({ empId: emp.id, in: emp.in_time || '', out: emp.out_time || '' }); setIsEditModalOpen(true); }} className="bg-slate-100 hover:bg-slate-200 text-slate-600 p-2 rounded-md"><Edit2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isEditModalOpen && editData && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white p-8 rounded-2xl shadow-2xl w-96 border border-slate-200">
            <h3 className="text-xl font-black mb-6 text-slate-800">Admin Edit Time</h3>
            <div className="space-y-4">
               <label className="text-xs font-bold text-slate-500 uppercase">Punch In</label>
               <input type="time" step="1" value={editData.in} onChange={e => setEditData({...editData, in: e.target.value})} className="w-full border rounded-lg p-3 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none" />
               <label className="text-xs font-bold text-slate-500 uppercase">Punch Out</label>
               <input type="time" step="1" value={editData.out} onChange={e => setEditData({...editData, out: e.target.value})} className="w-full border rounded-lg p-3 bg-slate-50 focus:ring-2 focus:ring-blue-500 outline-none" />
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setIsEditModalOpen(false)} className="px-5 py-2 text-slate-500 font-bold hover:bg-slate-100 rounded-xl transition">Cancel</button>
              <button onClick={saveManualAttendance} className="px-6 py-2 bg-blue-600 text-white font-bold hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-200">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyAttendance;